import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'

/**
 * The small status glyph inside the "Weapon claimed" button: a spinner while the
 * claim settles, then a tick that draws itself, and only then does the page move
 * on.
 *
 * It owns the whole sequence and reports the end through `onDone`, rather than
 * the page running a timer of its own alongside it. Two timelines for one
 * animation is how they drift: the fade would start on the page's schedule and
 * the tick would still be drawing, or finish early and sit there. Here the tick
 * finishing IS the signal, so they cannot disagree.
 */

/** Spinner time. Long enough to read as work happening, not so long that a
 *  finished claim feels withheld. */
const LOADING_MS = 1500
/** Spinner bounces out; the tick bounces in behind it. Matches the
 *  `bounce-out` / `bounce-in` durations in the Tailwind config. */
const SWAP_MS = 240
/** The stroke drawing itself, once the tick has landed. */
const DRAW_MS = 420

/** What the caller must budget for, if it needs to know. */
export const CLAIM_INDICATOR_MS = LOADING_MS + SWAP_MS + DRAW_MS

type Phase = 'loading' | 'swapping' | 'done'

export const ClaimIndicator = ({ onDone, color }: { onDone: () => void; color: string }) => {
    const [phase, setPhase] = useState<Phase>('loading')
    // Separate from `phase` because the stroke has to be mounted at full offset
    // for a frame before it animates -- a transition from a value the element
    // has never held does not run, and the tick would simply appear.
    const [drawn, setDrawn] = useState(false)

    useEffect(() => {
        const swap = setTimeout(() => setPhase('swapping'), LOADING_MS)
        const tick = setTimeout(() => setPhase('done'), LOADING_MS + SWAP_MS)
        const finish = setTimeout(() => onDone(), CLAIM_INDICATOR_MS)
        return () => {
            clearTimeout(swap)
            clearTimeout(tick)
            clearTimeout(finish)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (phase !== 'done') return
        const id = requestAnimationFrame(() => requestAnimationFrame(() => setDrawn(true)))
        return () => cancelAnimationFrame(id)
    }, [phase])

    return (
        // A fixed box, so the label beside it does not shift as the glyph swaps.
        <span className='relative inline-flex h-5 w-5 shrink-0 items-center justify-center'>
            {phase !== 'done' ? (
                <svg
                    viewBox='0 0 24 24'
                    className={twMerge(
                        'h-5 w-5 animate-spin',
                        phase === 'swapping' && 'animate-bounce-out',
                    )}
                    style={{ color }}
                    aria-hidden='true'
                >
                    {/* Track and arc: the arc is what reads as motion, the track
                        stops the spinner looking like a lone comma. */}
                    <circle cx='12' cy='12' r='9' fill='none' stroke='currentColor' strokeWidth='2.5' opacity='0.25' />
                    <path
                        d='M21 12a9 9 0 0 0-9-9'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2.5'
                        strokeLinecap='round'
                    />
                </svg>
            ) : (
                <svg viewBox='0 0 24 24' className='h-5 w-5 animate-bounce-in' style={{ color }} aria-hidden='true'>
                    <path
                        d='M4.5 12.5 L10 18 L19.5 6.5'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2.75'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        // `pathLength` normalises the stroke to 1 unit, so the
                        // dash maths does not depend on the path's real length
                        // and the tick can be redrawn without retuning it.
                        pathLength={1}
                        strokeDasharray={1}
                        strokeDashoffset={drawn ? 0 : 1}
                        style={{ transition: `stroke-dashoffset ${DRAW_MS}ms ease-out` }}
                    />
                </svg>
            )}
        </span>
    )
}
